import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import PayablesTable, { PAYABLES_EXPORT_COLUMNS } from "./PayablesTable";
import { exportTable } from "../../../utils/exportTable";
import { toast, confirmDialog } from "../../../components/Toast/toast";
import { AuthContext } from "../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../utils/constants/permissions";
import { btn } from "../../../components/Button/buttonStyles";

const PayablesPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  // Account managers view payables only - but they own vendor invoicing, so
  // the "add to new invoices" actions stay available to them.
  const readOnly = isReadOnly(user, MODULES.PAYABLES);
  const [payables, setPayables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  const fetchPayables = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await api.get("/project-finances/payables", { withCredentials: true });
      setPayables(response.data.data || []);
    } catch (err) {
      console.error("Error fetching payables:", err);
      setError("Failed to fetch payables");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayables();
  }, []);

  const handleToggle = (rowKey) => {
    setSelectedIds((prev) =>
      prev.includes(rowKey) ? prev.filter((k) => k !== rowKey) : [...prev, rowKey]
    );
  };

  const handleToggleAll = (data) => {
    const allKeys = data.map((r) => `${r.type}-${r.id}`);
    const allSelected = allKeys.every((k) => selectedIds.includes(k));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((k) => !allKeys.includes(k)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...allKeys])]);
    }
  };

  const handleEdit = (row) => {
    if (row.type === "flat_rate") {
      navigate(`/project/${row.project_id}/job/${row.job_id}/edit-flat-rate-receivable/${row.id}`);
    } else {
      navigate(`/project/${row.project_id}/job/${row.job_id}/edit-unit-based-receivable/${row.id}`);
    }
  };

  const handleDelete = async (row) => {
    if (!await confirmDialog(`Delete payable ${row.payable_code || ""}?`)) return;
    const endpoint =
      row.type === "flat_rate"
        ? `/project-finances/flat-rate-payables/${row.id}`
        : `/project-finances/unit-based-payables/${row.id}`;
    try {
      const { data } = await api.delete(endpoint, { withCredentials: true });
      if (data.success) {
        setSelectedIds((prev) => prev.filter((k) => k !== `${row.type}-${row.id}`));
        fetchPayables();
      }
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleClone = async (row) => {
    const endpoint =
      row.type === "flat_rate"
        ? "/project-finances/flat-rate-payables"
        : "/project-finances/unit-based-payables";

    const {
      id, createdAt, updatedAt, type,
      project, job, currency, unit, file, vendorInvoice,
      ...payload
    } = row;

    try {
      const jobRes = await api.get(`/jobs/${row.job_id}`, { withCredentials: true });
      const jobData = jobRes.data.data;

      const { data } = await api.post(
        endpoint,
        {
          ...payload,
          project_id: Number(row.project_id),
          job_id: Number(row.job_id),
          client_id: jobData.project?.client_id,
          vendor_id: jobData.vendor_id,
        },
        { withCredentials: true }
      );
      if (data.success) fetchPayables();
    } catch (err) {
      console.error("Clone failed:", err);
      toast.error(err.response?.data?.message || "Failed to clone payable.");
    }
  };

  const createInvoices = async (items) => {
    if (items.length === 0) return;
    setInvoiceLoading(true);
    try {
      const { data } = await api.post(
        "/project-finances/vendor-invoices",
        { payables: items.map((key) => { const [type, id] = key.split("-"); return { type, id: Number(id) }; }) },
        { withCredentials: true }
      );
      if (data.success) {
        setSelectedIds([]);
        const created = data.data || [];
        if (created.length === 1) {
          sessionStorage.setItem("invoiceSuccessMsg", "1 invoice has been created.");
          navigate(`/vendor/invoice/${created[0].id}`);
        } else {
          sessionStorage.setItem("invoiceSuccessMsg", `${created.length} invoice(s) have been created.`);
          navigate("/vendor/invoices");
        }
      }
    } catch (err) {
      console.error("Create invoice failed:", err);
      toast.error("Failed to create invoice(s).");
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleAddSelectionToInvoice = () => createInvoices(selectedIds);

  const handleAddFilterToInvoice = () => {
    const eligible = payables
      .filter((r) => !r.vendorInvoice && r.job?.status === "Completion Accepted")
      .map((r) => `${r.type}-${r.id}`);
    createInvoices(eligible);
  };

  const handleExportToExcel = () => {
    exportTable("vendor-payables", PAYABLES_EXPORT_COLUMNS, payables);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Payables</h1>
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={handleAddSelectionToInvoice}
            disabled={selectedIds.length === 0 || invoiceLoading}
            className={btn("primary", "sm")}
          >
            {invoiceLoading ? "Creating..." : "Add (selection) to new invoices"}
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={handleAddFilterToInvoice}
            disabled={invoiceLoading}
            className={btn("primary", "sm")}
          >
            {invoiceLoading ? "Creating..." : "Add (filter results) to new invoices"}
          </button>
          <button
            onClick={handleExportToExcel}
            className={btn("secondary", "sm")}
          >
            Export to Excel
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
        </div>
      )}
      {error && <p className="text-danger-600 text-sm">{error}</p>}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{payables.length}</span> items.
          </p>

          <PayablesTable
            data={payables}
            readOnly={readOnly}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onClone={handleClone}
            selectedIds={selectedIds}
            onToggle={handleToggle}
            onToggleAll={handleToggleAll}
          />
        </>
      )}
    </div>
  );
};

export default PayablesPage;
